/**
 * List state (filters, sort, paging) held in the URL.
 *
 * Keeping it in the query string rather than component state means a filtered view is
 * shareable, bookmarkable, and survives back/forward — which is what staff actually do
 * with "all unpaid motor policies expiring this month". It also means the browser, not
 * the component, is the source of truth, so there is no state to resynchronise.
 */

import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE } from '@ias/shared';

export interface ListState {
  /** Everything the API expects, already coerced. */
  params: Record<string, string | number>;
  page: number;
  perPage: number;
  sortBy: string | undefined;
  sortOrder: 'asc' | 'desc';
  search: string;
  /** Only the user-set filters — excludes paging and sorting. */
  filters: Record<string, string>;
  activeFilterCount: number;
  setFilter: (key: string, value: string | string[] | undefined) => void;
  setFilters: (next: Record<string, string | string[] | undefined>) => void;
  setSearch: (value: string) => void;
  setSort: (column: string) => void;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  clearFilters: () => void;
}

const RESERVED = new Set(['page', 'perPage', 'sortBy', 'sortOrder', 'tab']);

export function useListState(options?: {
  defaultSortBy?: string;
  defaultSortOrder?: 'asc' | 'desc';
  defaultPerPage?: number;
  /** Filters applied on every request and hidden from the "clear" affordance. */
  fixed?: Record<string, string>;
}): ListState {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page') ?? 1);
  const perPage = Number(searchParams.get('perPage') ?? options?.defaultPerPage ?? DEFAULT_PAGE_SIZE);
  const sortBy = searchParams.get('sortBy') ?? options?.defaultSortBy;
  const sortOrder = (searchParams.get('sortOrder') ?? options?.defaultSortOrder ?? 'asc') as 'asc' | 'desc';
  const search = searchParams.get('search') ?? '';

  const filters = React.useMemo(() => {
    const result: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (!RESERVED.has(key) && value) result[key] = value;
    });
    return result;
  }, [searchParams]);

  const update = React.useCallback(
    (mutate: (next: URLSearchParams) => void, resetPage = true) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          mutate(next);
          // Any change to what is being listed invalidates the current page number —
          // page 4 of a freshly narrowed result set is usually empty.
          if (resetPage) next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setFilter = React.useCallback(
    (key: string, value: string | string[] | undefined) => {
      update((next) => {
        const serialised = Array.isArray(value) ? value.join(',') : value;
        if (!serialised) next.delete(key);
        else next.set(key, serialised);
      });
    },
    [update],
  );

  const setFilters = React.useCallback(
    (values: Record<string, string | string[] | undefined>) => {
      update((next) => {
        for (const [key, value] of Object.entries(values)) {
          const serialised = Array.isArray(value) ? value.join(',') : value;
          if (!serialised) next.delete(key);
          else next.set(key, serialised);
        }
      });
    },
    [update],
  );

  const setSearch = React.useCallback(
    (value: string) => {
      update((next) => {
        if (value) next.set('search', value);
        else next.delete('search');
      });
    },
    [update],
  );

  /**
   * Click cycles asc → desc → unsorted, which is what a spreadsheet user expects.
   *
   * And, like a spreadsheet, it shows the top of the new order. The page number used to
   * survive a sort click, so a click on «Λήξη» from page 3 answered with page 3 of the
   * re-ordered book — rows from the middle, in an order that looked like no order at all.
   * The report that came back was «το sorting δεν παίζει», and it was right: a page number
   * belongs to one ordering, and the click just discarded that ordering.
   */
  const setSort = React.useCallback(
    (column: string) => {
      update((next) => {
        const currentBy = next.get('sortBy') ?? options?.defaultSortBy;
        const currentOrder = next.get('sortOrder') ?? options?.defaultSortOrder ?? 'asc';

        if (currentBy !== column) {
          next.set('sortBy', column);
          next.set('sortOrder', 'asc');
        } else if (currentOrder === 'asc') {
          next.set('sortOrder', 'desc');
        } else {
          next.delete('sortBy');
          next.delete('sortOrder');
        }
      });
    },
    [update, options?.defaultSortBy, options?.defaultSortOrder],
  );

  const setPage = React.useCallback(
    (value: number) => update((next) => next.set('page', String(value)), false),
    [update],
  );

  const setPerPage = React.useCallback(
    (value: number) => update((next) => next.set('perPage', String(value))),
    [update],
  );

  const clearFilters = React.useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const params = React.useMemo(
    () => ({
      ...options?.fixed,
      ...filters,
      page,
      perPage,
      ...(sortBy ? { sortBy, sortOrder } : {}),
    }),
    [filters, page, perPage, sortBy, sortOrder, options?.fixed],
  );

  return {
    params,
    page,
    perPage,
    sortBy,
    sortOrder,
    search,
    filters,
    activeFilterCount: Object.keys(filters).filter((k) => k !== 'search').length + (search ? 1 : 0),
    setFilter,
    setFilters,
    setSearch,
    setSort,
    setPage,
    setPerPage,
    clearFilters,
  };
}

/** Debounced mirror of the URL search value, so typing does not fire a request per key. */
export function useDebouncedSearch(value: string, onChange: (value: string) => void, delay = 300) {
  const [local, setLocal] = React.useState(value);
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  // Re-sync when the URL changes from outside (back button, "clear filters").
  React.useEffect(() => setLocal(value), [value]);

  React.useEffect(() => {
    if (local === value) return;
    const timer = setTimeout(() => onChangeRef.current(local), delay);
    return () => clearTimeout(timer);
  }, [local, value, delay]);

  return [local, setLocal] as const;
}
